import { Component, Input } from '@angular/core';
import { User, UserAvatarData } from '@completion/models';
import { UserPreference } from '../../shared/enums/user-preference';

@Component({
  selector: 'app-user-avatar',
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  standalone: false,
})
export class UserAvatarComponent {
  private _user: UserAvatarData | null = null;

  /**
   * When true the avatar circle is coloured by calendar-consent status instead
   * of project role.  Defaults to false so existing usages are unaffected.
   */
  @Input() showCalendarConsent = false;

  @Input() set user(value: User | UserAvatarData | null) {
    if (!value) {
      this._user = null;
      return;
    }
    // Map User (has userName/id) to UserAvatarData shape
    if ('userName' in value) {
      // Derive consent from the preferences array (API sends {id, name, value} objects).
      // Fall back to the explicit calendarConsentGranted field if present.
      const consentEntry = value.preferences?.find((p) => p.name === UserPreference.CONSENT_TO_CALENDAR_INVITES);
      const calendarConsentGranted = consentEntry ? consentEntry.value === 'true' : (value.calendarConsentGranted ?? undefined);

      this._user = {
        name: value.name,
        email: value.email,
        sapId: value.sapId,
        calendarConsentGranted,
      };
    } else {
      this._user = value;
    }
  }

  get user(): UserAvatarData | null {
    return this._user;
  }

  get initials(): string {
    const source = this.user?.name || this.user?.email || this.user?.sapId || '';
    return source ? source.charAt(0).toUpperCase() : '?';
  }

  get displayName(): string {
    return this.user?.name || this.user?.email || this.user?.sapId || '';
  }

  get displayEmail(): string | null {
    return this.user?.email || null;
  }

  get displaySapId(): string | null {
    return this.user?.sapId || null;
  }

  /**
   * Map role to avatar color CSS class.
   * When showCalendarConsent is true, consent status takes priority:
   *   true  → light-green / dark-green
   *   false → light-red   / dark-red
   *   null/undefined → light-blue / dark-blue  (no record)
   */
  get avatarClass(): string {
    if (this.showCalendarConsent) {
      const consent = this.user?.calendarConsentGranted;
      if (consent === true) return 'avatar-consent-granted';
      if (consent === false) return 'avatar-consent-declined';
      return 'avatar-consent-unknown';
    }

    const roleKey = (this.user?.role || '').toString().toUpperCase();
    switch (roleKey) {
      case 'ADMIN':
        return 'avatar-admin';
      case 'EDITOR':
        return 'avatar-editor';
      case 'OPERATOR_CP':
      case 'MANAGER':
        return 'avatar-operator-cp';
      case 'OPERATOR_MC':
      case 'OPERATOR_MCP':
        return 'avatar-operator-mc';
      case 'COMPANY_SIGN_CERT':
        return 'avatar-company-sign-cert';
      case 'VENDOR':
        return 'avatar-vendor';
      default:
        return 'avatar-read-only';
    }
  }
}
